import { Typography } from '@mui/material';
import { useErrorFullPageStyles } from './error-full-page.styles';

export interface ErrorDto {
  title?: string | ((entity?: string) => JSX.Element | string);
  message?: string | ((entity?: string) => JSX.Element | string);
  redirectUrl?: string;
}

export interface ErrorFullPageProps {
  error?: ErrorDto;
  preset?: Preset;
  entity?: string;
}

/**
 * The full page error component is a presentational component that displays a full screen error message.
 */
export function ErrorFullPage({ error, preset, entity }: ErrorFullPageProps) {
  const classes = useErrorFullPageStyles();

  let errorPreset = presets.DEFAULT;

  if (preset) errorPreset = presets[preset];

  if (error) errorPreset = error;

  return (
    <>
      <Typography variant="h3" className={classes.title}>
        {typeof errorPreset.title === 'function' ? errorPreset.title(entity) : errorPreset.title}
      </Typography>
      <Typography variant="h6" className={classes.message}>
        {typeof errorPreset.message === 'function' ? errorPreset.message(entity) : errorPreset.message}
      </Typography>
    </>
  );
}

type Preset = 'GLOBAL_ACCESS_DENIED' | 'ACCESS_DENIED' | 'GLOBAL_NOT_FOUND' | 'NOT_FOUND';

const presets: { [key in Preset | 'DEFAULT']: ErrorDto } = {
  DEFAULT: {
    title: 'Something went wrong',
    message: 'Sorry, an unexpected error has occured.'
  },
  GLOBAL_ACCESS_DENIED: {
    title: 'Not much to see here...',
    message: () => (
      <>
        Unfortunately you don’t have permission to access anything. Have you recently accepted an invitation to join?
        You may not have completed the process by clicking on the Accept invite button. You can retry by following the
        link in your invitation email. <br />
        <br /> If you believe you should still have access, please get in touch with your building manager or contact
        us.
      </>
    )
  },
  ACCESS_DENIED: {
    title: `You shouldn't be here...`,
    message: (entity?: string) => `Sorry, you don't have permission to access this ${entity || 'project'}. 
      ${entity === 'project' ? 'Please choose a project from the menu. ' : ''}If you believe you should have access, 
      please get in touch with your building manager or contact us.`
  },
  GLOBAL_NOT_FOUND: {
    title: 'Page not found...',
    message: `Sorry we couldn't find the page you were looking for`
  },
  NOT_FOUND: {
    title: (entity?: string) => `We couldn't find this ${entity || 'project'}...`,
    message: (entity?: string) =>
      `Sorry, the requested ${entity || 'project'} does not seem to exist. ${
        !entity || entity === 'project' ? 'Please choose a project from the menu' : ''
      }`
  }
};
